import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Search, Plus, ChevronLeft, ChevronRight, Edit, Trash2, Calendar, 
  Check, X, Home, BellDot, ShoppingCart, ShoppingBag, Settings,
  Tag, RefreshCw, Sparkles, CheckCircle, ChevronDown, ChevronUp
} from "lucide-react";
import MobileBottomNav from "./MobileBottomNav";
import { useTranslation } from '../../../contexts/LanguageContext';
import { useAuth } from "../../../App"; // Import the auth hook

const MobileSubscriptionList = ({ subscriptions, onAddSubscription }) => {
  const { t } = useTranslation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Get auth context
  const navigate = useNavigate();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedSubscription, setExpandedSubscription] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const [editedSubscription, setEditedSubscription] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [availablePlans, setAvailablePlans] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCustomPlanForm, setShowCustomPlanForm] = useState(false);
  const [expandedPlanDetails, setExpandedPlanDetails] = useState({});
  const [customPlanData, setCustomPlanData] = useState({
    name: '',
    price: '',
    interval: 'monthly'
  });
  const itemsPerPage = 5;

  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return t('common.na', 'N/A');
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getBillingText = (interval, price, currency = 'EUR') => {
    const formattedPrice = price?.toFixed?.(2) || '0.00';
    const currencySymbol = currency === 'EUR' ? '€' : currency;
    
    switch (interval) {
      case 'monthly': return `${currencySymbol}${formattedPrice}/mo`;
      case 'annual': return `${currencySymbol}${formattedPrice}/yr`;
      case 'quarterly': return `${currencySymbol}${formattedPrice}/qtr`;
      default: return `${currencySymbol}${formattedPrice}`;
    }
  };

  // Filter and pagination
  const filteredSubscriptions = subscriptions.filter(sub => 
    sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.billing_interval?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredSubscriptions.length / itemsPerPage);
  const currentSubscriptions = filteredSubscriptions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Fetch merchant plans - UPDATED TO USE httpClient
  const fetchMerchantPlans = async (merchantId) => {
    try {
      setLoadingPlans(true);
      
      if (!httpClient || !merchantId) {
        console.error("HTTP client not available or merchant ID missing");
        return null;
      }
      
      const response = await httpClient.request(
        `/api/subscriptions/available-plans?merchant_id=${merchantId}`
      );
      
      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return null;
        }
        // Retry the request
        return fetchMerchantPlans(merchantId);
      }
      
      if (!response.ok) {
        console.warn('Failed to fetch merchant plans:', response.status);
        return null;
      }
      
      const data = await response.json();
      
      if (data.success) {
        return {
          merchant: {
            name: data.merchant_name,
            has_affiliate: data.has_affiliate || false
          },
          plans: data.available_plans || [],
          current_plan: data.current_plan
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching available plans:', error);
      return null;
    } finally {
      setLoadingPlans(false);
    }
  };

  // Handlers
  const handleEditClick = async (subscription, e) => {
    e.stopPropagation();
    
    // Check authentication
    if (!user) {
      navigate("/demo");
      return;
    }
    
    setEditedSubscription({ ...subscription });
    setExpandedSubscription(subscription.id === expandedSubscription ? null : subscription.id);
    setSaveSuccess(false);
    setAvailablePlans(null);
    setShowCustomPlanForm(false);
    setExpandedPlanDetails({});
    setCustomPlanData({
      name: '',
      price: '',
      interval: 'monthly'
    });
    
    // Fetch merchant plans
    if (subscription.merchant_id) {
      setLoadingPlans(true);
      try {
        const plansData = await fetchMerchantPlans(subscription.merchant_id);
        setAvailablePlans(plansData);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      } finally {
        setLoadingPlans(false);
      }
    }
  };

  const togglePlanDetails = (planId) => {
    setExpandedPlanDetails(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const handleSelectPlan = async (plan) => {
    try {
      setIsUpdating(true);
      
      if (!httpClient) {
        throw new Error("HTTP client not available");
      }

      const updates = {
        plan_details: {
          plan_id: plan.id,
          plan_name: plan.name,
          price: plan.price,
          billing_interval: plan.billing_interval,
          currency: plan.currency || 'EUR',
          is_custom: false
        }
      };

      const response = await httpClient.request('/api/update-subscription', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: editedSubscription.merchant_id,
          updates: updates
        }),
      });

      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return handleSelectPlan(plan);
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('subscriptionList.updateFailed', 'Failed to update subscription'));
      }

      setSaveSuccess(true);
      setTimeout(() => {
        setExpandedSubscription(null);
        setSaveSuccess(false);
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Plan change failed:', error);
      alert(`${t('subscriptionList.updateFailed', 'Update failed')}: ${error.message}`);
      setIsUpdating(false);
    }
  };

  const handleSaveCustomPlan = async () => {
    if (!customPlanData.name.trim()) {
      alert(t('subscriptionList.planNameRequired', 'Plan name is required'));
      return;
    }

    const price = parseFloat(customPlanData.price);
    if (isNaN(price) || price <= 0) {
      alert(t('subscriptionList.validPriceRequired', 'Please enter a valid price'));
      return;
    }

    try {
      setIsUpdating(true);
      
      if (!httpClient) {
        throw new Error("HTTP client not available");
      }

      const updates = {
        plan_details: {
          plan_id: `custom-${Date.now()}`,
          plan_name: customPlanData.name.trim(),
          price: price,
          billing_interval: customPlanData.interval,
          currency: editedSubscription.currency || 'EUR',
          is_custom: true
        }
      };

      const response = await httpClient.request('/api/update-subscription', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: editedSubscription.merchant_id,
          updates: updates
        }),
      });

      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return handleSaveCustomPlan();
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('subscriptionList.updateFailed', 'Failed to create custom plan'));
      }

      setSaveSuccess(true);
      setTimeout(() => {
        setExpandedSubscription(null);
        setSaveSuccess(false);
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Custom plan creation failed:', error);
      alert(`${t('subscriptionList.updateFailed', 'Update failed')}: ${error.message}`);
      setIsUpdating(false);
    }
  };

  const handleUpdateSubscription = async (e) => {
    e.stopPropagation();
    
    try {
      setIsUpdating(true);
      
      if (!httpClient) {
        throw new Error("HTTP client not available");
      }

      const response = await httpClient.request('/api/update-subscription', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: editedSubscription.merchant_id,
          updates: {
            next_billing_date: editedSubscription.next_billing_date
          }
        }),
      });

      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return handleUpdateSubscription(e);
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('subscriptionList.updateFailed', 'Failed to update subscription'));
      }

      setSaveSuccess(true);
      setTimeout(() => {
        setExpandedSubscription(null);
        setSaveSuccess(false);
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Update failed:', error);
      alert(`${t('subscriptionList.updateFailed', 'Update failed')}: ${error.message}`);
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = (subscription, e) => {
    e.stopPropagation();
    setSubscriptionToDelete(subscription);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!subscriptionToDelete) return;
    
    try {
      setIsUpdating(true);
      
      if (!httpClient) {
        throw new Error("HTTP client not available");
      }

      const response = await httpClient.request('/api/delete-subscription', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: subscriptionToDelete.merchant_id || subscriptionToDelete.id
        }),
      });

      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return confirmDelete();
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || t('subscriptionList.deleteFailed', 'Deletion failed'));
      }

      window.location.reload();
    } catch (error) {
      alert(`${t('common.error', 'Error')}: ${error.message}`);
      setIsUpdating(false);
    } finally {
      setShowConfirmDelete(false);
      setSubscriptionToDelete(null);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedSubscription(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Render loading overlay for mobile
  const renderLoadingOverlay = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] pointer-events-auto">
      <div className="bg-white p-6 rounded-xl shadow-xl mx-4 w-full max-w-sm transform transition-all duration-300 scale-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-b-4 border-[#253985] mx-auto mb-4"></div>
          <h2 className="text-lg font-bold mb-2 text-gray-800">
            {t('subscriptionList.updatingSubscription', 'Updating your subscription')}
          </h2>
          <p className="text-sm text-gray-600">
            {t('subscriptionList.pleaseWait', 'Please wait while we process your changes...')}
          </p>
          <div className="mt-3 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
            <div className="animate-progress h-full bg-[#253985] rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render plan list for mobile
  const renderPlanList = () => {
    if (loadingPlans) {
      return (
        <div className="flex flex-col items-center justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#253985] mb-2"></div>
          <p className="text-sm text-gray-600">{t('subscriptionList.loadingPlans', 'Loading available plans...')}</p>
        </div>
      );
    }

    if (!availablePlans?.plans || availablePlans.plans.length === 0) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-yellow-800 text-sm">
            {t('subscriptionList.noPlansAvailable', 'No standard plans available for this merchant.')}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">
            {t('subscriptionList.availablePlans', 'Available Plans')}
          </h4>
          <span className="text-xs text-gray-500">
            {availablePlans.plans.length} {t('subscriptionList.plansAvailable', 'plans')}
          </span>
        </div>
        
        {availablePlans.plans.map((plan) => {
          const isExpanded = expandedPlanDetails[plan.id];
          const isCurrentPlan = editedSubscription.plan_id === plan.id;
          
          return (
            <div
              key={plan.id || `plan-${Date.now()}-${Math.random()}`}
              onClick={() => !isCurrentPlan && handleSelectPlan(plan)}
              className={`border rounded-lg ${isCurrentPlan ? 'cursor-default' : 'cursor-pointer'} ${
                isCurrentPlan
                  ? 'border-[#253985] bg-blue-50'
                  : 'border-gray-200 hover:border-[#253985]/50'
              }`}
            >
              {/* Plan Header */}
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center space-x-2">
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                    isCurrentPlan 
                      ? 'border-[#253985] bg-[#253985]' 
                      : 'border-gray-300'
                  }`}>
                    {isCurrentPlan && (
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <h5 className="text-sm font-medium text-gray-800 truncate">
                        {plan.name}
                      </h5>
                      {isCurrentPlan && (
                        <span className="ml-2 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {t('subscriptionList.current', 'Current')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 mt-0.5">
                      <span className="text-xs font-medium text-[#253985]">
                        {getBillingText(plan.billing_interval, plan.price, plan.currency)}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {plan.billing_interval === 'monthly' ? t('common.monthly', 'Monthly') : t('common.annual', 'Annual')}
                      </span>
                    </div>
                  </div>
                </div>
                
                {plan.features && plan.features.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlanDetails(plan.id);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
              
              {/* Expandable Details */}
              {isExpanded && plan.features && plan.features.length > 0 && (
                <div className="px-3 pb-3 border-t border-gray-200 pt-2">
                  <h6 className="text-xs font-medium text-gray-700 mb-1">
                    {t('subscriptionList.features', 'Features')}
                  </h6>
                  <div className="space-y-1">
                    {plan.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-1">
                        <Check className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-gray-600">{feature}</span>
                      </div>
                    ))}
                    {plan.features.length > 3 && (
                      <div className="text-xs text-gray-500">
                        +{plan.features.length - 3} {t('subscriptionList.moreFeatures', 'more')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Mobile bottom navigation items
  const navItems = [
    { icon: Home, label: t('bottomNav.home', 'Home'), path: "/mobile-dashboard" },
    { icon: BellDot, label: t('bottomNav.notifications', 'Notifications'), path: "/mobile-notifications" },
    { icon: ShoppingCart, label: t('bottomNav.marketplace', 'Marketplace'), path: "/mobile-marketplace" },
    { icon: ShoppingBag, label: t('bottomNav.connectBank', 'Connect Bank'), path: "/mobile-connectBank" },
    { icon: Settings, label: t('bottomNav.settings', 'Settings'), path: "/mobile-settings" },
  ];

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Show loading overlay when updating */}
      {isUpdating && renderLoadingOverlay()}
      
      <div className="flex-1 overflow-auto pb-20">
        <div className="p-4 space-y-4">
          {/* Search and Add */}
          <div className="flex flex-col gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('subscriptionList.searchPlaceholder', 'Search subscriptions...')}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <button
              onClick={() => navigate('/mobile-marketplace')}
              className="w-full flex items-center justify-center px-4 py-2 bg-[#253985] text-white rounded-lg"
            >
              <Plus className="h-5 w-5 mr-2" />
              {t('subscriptionList.addSubscription', 'Add Subscription')}
            </button>
          </div>

          {/* Subscription List */}
          <div className="space-y-2">
            {currentSubscriptions.map((subscription) => (
              <div key={subscription.id} className="bg-white p-3 rounded-lg border border-gray-200">
                {/* Subscription Header */}
                <div className="flex items-center justify-between" 
                  onClick={() => navigate(`/mobile-merchant/${subscription.merchant_id}`)}>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      {subscription.logo ? (
                        <img 
                          src={subscription.logo} 
                          alt={subscription.name} 
                          className="w-8 h-8 object-contain"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiMyNTM5ODUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMyA5bDktNyA5IDd2MTFhMiAyIDAgMCAxLTIgMkg1YTIgMiAwIDAgMS0yLTJ6Ij48L3BhdGg+PHBvbHlsaW5lIHBvaW50cz0iOSAyMiA5IDEyIDE1IDEyIDE1IDIyIj48L3BvbHlsaW5lPjwvc3ZnPg==';
                          }}
                        />
                      ) : (
                        <div className="text-[#253985]">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                            <polyline points="9 22 9 12 15 12 15 22"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center">
                        <h4 className="font-medium text-gray-800">{subscription.name}</h4>
                        {subscription.is_custom && (
                          <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded-full">
                            <Tag className="w-3 h-3 inline mr-1" />
                            Custom
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {subscription.billing_interval ? 
                          `${subscription.billing_interval.charAt(0).toUpperCase()}${subscription.billing_interval.slice(1)} ${t('subscriptionList.plan', 'plan')}` : 
                          t('subscriptionList.monthlyPlan', 'Monthly plan')}
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={(e) => handleEditClick(subscription, e)}
                    className="p-1 text-gray-500 hover:text-[#253985]"
                    aria-label={t('common.edit', 'Edit')}
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                </div>

                {/* Subscription Details */}
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('common.price', 'Price')}:</span>
                    <span className="font-medium">
                      {getBillingText(subscription.billing_interval, subscription.price, subscription.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('subscriptionList.nextPayment', 'Next Payment')}:</span>
                    <span className="text-gray-700">
                      {formatDate(subscription.next_billing_date)}
                    </span>
                  </div>
                </div>

                {/* Edit Section */}
                {expandedSubscription === subscription.id && (
                  <div className="mt-3 space-y-4 pt-3 border-t border-gray-200">
                    {/* Billing Date Update */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-[#253985]">
                        {t('subscriptionList.updateBillingDate', 'Update Billing Date')}
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-2 top-2.5 h-5 w-5 text-gray-400" />
                        <input
                          type="date"
                          value={editedSubscription?.next_billing_date?.split('T')[0] || ''}
                          onChange={(e) => handleFieldChange('next_billing_date', e.target.value)}
                          className="w-full pl-8 p-2 border border-gray-300 rounded-lg bg-white"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        {t('subscriptionList.billingDateHelp', 'Update the next billing date')}
                      </p>
                    </div>

                    {/* Plan Selection */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-[#253985]">
                          {t('subscriptionList.changePlan', 'Change Plan')}
                        </h4>
                        {availablePlans?.merchant?.has_affiliate && (
                          <span className="text-xs text-green-600 flex items-center">
                            <Sparkles className="w-3 h-3 mr-1" />
                            {t('subscriptionList.affiliateAvailable', 'Affiliate offers')}
                          </span>
                        )}
                      </div>
                      
                      {/* Plan List */}
                      {renderPlanList()}
                      
                      {/* Custom Plan Option */}
                      {!showCustomPlanForm ? (
                        <div
                          onClick={() => setShowCustomPlanForm(true)}
                          className="p-4 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all text-center"
                        >
                          <div className="inline-flex items-center justify-center w-10 h-10 bg-purple-100 rounded-full mb-2">
                            <Plus className="w-5 h-5 text-purple-600" />
                          </div>
                          <h5 className="text-sm font-medium text-purple-700">
                            {t('subscriptionList.createCustomPlan', 'Create Custom Plan')}
                          </h5>
                          <p className="text-xs text-gray-600 mt-1">
                            {t('subscriptionList.createCustomDescription', "Can't find your plan?")}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3 p-3 border border-purple-200 rounded-lg bg-purple-50">
                          <div className="flex items-center justify-between">
                            <h5 className="text-sm font-medium text-purple-800">
                              {t('subscriptionList.createCustomPlan', 'Create Custom Plan')}
                            </h5>
                            <button
                              onClick={() => setShowCustomPlanForm(false)}
                              className="text-purple-600 hover:text-purple-800 p-0.5"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="text-xs font-medium text-gray-700 mb-1 block">
                                {t('subscriptionList.planName', 'Plan Name')}
                              </label>
                              <input
                                type="text"
                                value={customPlanData.name}
                                onChange={(e) => setCustomPlanData(prev => ({
                                  ...prev,
                                  name: e.target.value
                                }))}
                                className="w-full p-2 text-sm border border-gray-300 rounded-lg"
                                placeholder={t('subscriptionList.enterPlanName', 'Plan name')}
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs font-medium text-gray-700 mb-1 block">
                                  {t('common.price', 'Price')}
                                </label>
                                <div className="relative">
                                  <span className="absolute left-2 top-2 text-gray-500 text-sm">
                                    {editedSubscription.currency === 'EUR' ? '€' : editedSubscription.currency || '€'}
                                  </span>
                                  <input
                                    type="number"
                                    value={customPlanData.price}
                                    onChange={(e) => setCustomPlanData(prev => ({
                                      ...prev,
                                      price: e.target.value
                                    }))}
                                    className="w-full pl-6 p-2 text-sm border border-gray-300 rounded-lg"
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                  />
                                </div>
                              </div>
                              
                              <div>
                                <label className="text-xs font-medium text-gray-700 mb-1 block">
                                  {t('subscriptionList.billingInterval', 'Interval')}
                                </label>
                                <div className="flex space-x-1">
                                  {['monthly', 'annual'].map((interval) => (
                                    <button
                                      key={interval}
                                      type="button"
                                      onClick={() => setCustomPlanData(prev => ({
                                        ...prev,
                                        interval
                                      }))}
                                      className={`flex-1 py-2 text-xs text-center rounded transition-all ${
                                        customPlanData.interval === interval
                                          ? 'bg-purple-600 text-white'
                                          : 'bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      {interval === 'monthly' ? t('common.monthly', 'Monthly') : t('common.annual', 'Annual')}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-white border border-purple-200 rounded-lg p-2">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="text-xs font-medium text-gray-700">
                                    {customPlanData.name || t('subscriptionList.yourCustomPlan', 'Custom Plan')}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {customPlanData.price 
                                      ? `${editedSubscription.currency === 'EUR' ? '€' : editedSubscription.currency || '€'}${parseFloat(customPlanData.price || 0).toFixed(2)}/${customPlanData.interval === 'monthly' ? t('common.perMonthShort', 'mo') : t('common.perYearShort', 'yr')}`
                                      : t('subscriptionList.priceNotSet', 'Price not set')
                                    }
                                  </p>
                                </div>
                                <span className="flex items-center text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                                  <Tag className="w-3 h-3 mr-1" />
                                  {t('subscriptionList.custom', 'Custom')}
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex space-x-2">
                              <button
                                onClick={() => setShowCustomPlanForm(false)}
                                className="flex-1 py-2 text-xs border border-gray-300 rounded-lg text-gray-700"
                              >
                                {t('common.cancel', 'Cancel')}
                              </button>
                              <button
                                onClick={handleSaveCustomPlan}
                                disabled={!customPlanData.name.trim() || !customPlanData.price}
                                className={`flex-1 py-2 text-xs bg-purple-600 text-white rounded-lg ${
                                  (!customPlanData.name.trim() || !customPlanData.price) 
                                    ? 'opacity-50 cursor-not-allowed' 
                                    : ''
                                }`}
                              >
                                {t('subscriptionList.createPlan', 'Create')}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={handleUpdateSubscription}
                        className="w-full py-2 bg-[#253985] text-white rounded-lg text-sm font-medium"
                      >
                        {t('common.save', 'Save Changes')}
                      </button>
                      {saveSuccess && (
                        <span className="text-green-600 text-xs text-center flex items-center justify-center">
                          <Check className="h-3 w-3 inline mr-1" /> {t('subscriptionList.changesSaved', 'Changes saved!')}
                        </span>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={(e) => handleDeleteClick(subscription, e)}
                          className="py-2 text-red-600 border border-red-200 rounded-lg text-sm"
                        >
                          {t('subscriptionList.removeSubscription', 'Remove')}
                        </button>
                        <button
                          onClick={() => setExpandedSubscription(null)}
                          className="py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          {t('common.cancel', 'Cancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Empty State */}
            {currentSubscriptions.length === 0 && (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-600">
                  {searchTerm ? 
                    t('subscriptionList.noSearchResults', 'No subscriptions match your search') : 
                    t('dashboard.noSubscriptions', 'No subscriptions found')}
                </p>
                <button
                  onClick={() => navigate('/mobile-marketplace')}
                  className="mt-3 px-4 py-2 bg-[#253985] text-white rounded-lg"
                >
                  {t('dashboard.addFirstSubscription', 'Add Your First Subscription')}
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredSubscriptions.length > itemsPerPage && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 disabled:opacity-50"
                aria-label={t('common.previous', 'Previous')}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-sm">
                {t('common.page', 'Page')} {currentPage} {t('common.of', 'of')} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 disabled:opacity-50"
                aria-label={t('common.next', 'Next')}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showConfirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-4 w-full max-w-md">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">
                {t('subscriptionList.confirmDeletion', 'Confirm Deletion')}
              </h3>
              <button onClick={() => setShowConfirmDelete(false)}>
                <X className="h-6 w-6 text-gray-400" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-gray-600">
                {t('subscriptionList.deleteMessage', 'Delete')} <span className="font-medium text-[#253985]">{subscriptionToDelete?.name}</span>?
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {t('subscriptionList.cannotUndo', 'This action cannot be undone.')}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowConfirmDelete(false)}
                className="flex-1 py-2 border rounded-lg"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg"
              >
                {t('common.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <MobileBottomNav
        navItems={navItems}
        currentPath={window.location.pathname}
        navigate={navigate}
      />
    </div>
  );
};

export default MobileSubscriptionList;